import React, { useState, useEffect } from 'react';
import { useFocusEffect } from '@react-navigation/native';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Dimensions,
  SafeAreaView,
  StatusBar,
  ImageBackground,
  ActivityIndicator,
  Alert,
  Image,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Ionicons } from '@expo/vector-icons';
import { useFonts } from 'expo-font';
import SharedHeader from '../components/SharedHeader';
import TabNavigation from '../components/TabNavigation';
import FilterBadges from '../components/FilterBadges';
import SearchFilter from '../components/SearchFilter';
import {
  getCoupons,
  getCouponCategories,
  claimCoupon,
  getMyCoupons,
  formatCouponForDisplay,
  formatClaimedCouponForDisplay,
  formatCategoryForDisplay,
  buildClaimedCouponFromClaimResponse,
  getCouponImageSource,
  getDiscountText,
  isProductGiftCoupon,
  getHadiahProdukValidationCode,
  isDigitalRewardPending,
  getDigitalRewardSn,
  getDigitalRewardListStatusLabel,
} from '../services/couponsApi';
import { getPaygiftListStatusLabel, getPaygiftClaimModalUi, isPaygiftClaimPending } from '../utils/paygiftUserFacingMessage';
import { usePaygiftClaimBlockedModal } from '../components/PaygiftClaimBlockedModal';
import { navigateToClaimedCouponDetails } from '../navigation/navigateToClaimedCoupon';

const { width, height } = Dimensions.get('window');

export default function CouponsListScreen({ navigation }) {
  const { showFromError: showPaygiftBlockedFromError, modal: paygiftBlockedModal } = usePaygiftClaimBlockedModal();
  const [userData, setUserData] = useState(null);
  const [selectedTab, setSelectedTab] = useState('my-coupons');
  const [selectedFilter, setSelectedFilter] = useState('Semua');
  const [searchQuery, setSearchQuery] = useState('');
  
  // API data states
  const [coupons, setCoupons] = useState([]);
  const [myCoupons, setMyCoupons] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filters, setFilters] = useState(['Semua']);
  
  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [loadingMyCoupons, setLoadingMyCoupons] = useState(false);
  const [error, setError] = useState(null);
  
  // Pagination states (for Hot Coupon / catalog)
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  // Pagination states for Shopping Coupon (my-coupons)
  const [myCouponsCurrentPage, setMyCouponsCurrentPage] = useState(1);
  const [myCouponsTotalPages, setMyCouponsTotalPages] = useState(1);
  const [myCouponsHasMore, setMyCouponsHasMore] = useState(false);

  const tabs = [
    { key: 'my-coupons', label: 'Shopping Coupon' },
    { key: 'catalog', label: 'Hot Coupon' },
  ];

  const handleSearch = (query) => {
    setSearchQuery(query);
    // Reload coupons with search query
    if (selectedTab === 'catalog') {
      loadCoupons(1, selectedFilter, query);
    }
  };
  
  const handleFilterChange = (filter) => {
    setSelectedFilter(filter);
    setCurrentPage(1);
    if (selectedTab === 'catalog') {
      loadCoupons(1, filter, searchQuery);
    }
  };
  
  const [fontsLoaded] = useFonts({
    'Poppins': require('../../assets/fonts/Poppins-Regular.ttf'),
    'Poppins-Medium': require('../../assets/fonts/Poppins-Medium.ttf'),
    'Poppins-SemiBold': require('../../assets/fonts/Poppins-SemiBold.ttf'),
    'Poppins-Bold': require('../../assets/fonts/Poppins-Bold.ttf'),
  });

  useEffect(() => {
    loadUserData();
    loadCategories();
  }, []);
  
  useEffect(() => {
    setCurrentPage(1); // Reset pagination when tab or filters change
    if (selectedTab === 'catalog') {
      // Load both catalog coupons and my coupons for filtering
      loadCoupons(1, selectedFilter, searchQuery);
      loadMyCoupons(1); // Load claimed coupons to filter them out
    } else if (selectedTab === 'my-coupons') {
      // Clear catalog coupons data when switching to my coupons
      setCoupons([]);
      setMyCouponsCurrentPage(1);
      loadMyCoupons(1);
    }
  }, [selectedTab, selectedFilter, searchQuery]);

  // Refresh my coupons data when screen comes into focus
  useFocusEffect(
    React.useCallback(() => {
      if (selectedTab === 'my-coupons') {
        console.log('CouponsScreen focused - refreshing my coupons data');
        setMyCouponsCurrentPage(1);
        loadMyCoupons(1);
      }
    }, [selectedTab])
  );

  const loadUserData = async () => {
    try {
      const userToken = await AsyncStorage.getItem('userToken');
      const sessionId = await AsyncStorage.getItem('session_id');
      
      if (!userToken || !sessionId) {
        navigation.replace('Login');
        return;
      }
      
      const userDataString = await AsyncStorage.getItem('userData');
      if (userDataString) {
        const apiUserData = JSON.parse(userDataString);
        setUserData({
          ...apiUserData,
          name: apiUserData.user_fullname || apiUserData.name || 'User',
          points: apiUserData.points || 0,
          stamps: apiUserData.stamps || 0,
        });
      }
    } catch (error) {
      console.log('Error loading user data:', error);
      navigation.replace('Login');
    }
  };

  // Load coupon categories
  const loadCategories = async () => {
    try {
      const response = await getCouponCategories();
      if (response.status === 'success') {
        const formattedCategories = response.data.categories.map(formatCategoryForDisplay);
        setCategories(formattedCategories);
        
        // Update filters with categories, ensuring uniqueness
        const categoryNames = formattedCategories.map(cat => cat.name);
        const uniqueFilters = ['Semua', ...new Set(categoryNames)];
        setFilters(uniqueFilters);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
      // Keep default filters if API fails
      setFilters(['Semua', 'makanan', 'minuman']);
    }
  };
  
  // Load available coupons
  const loadCoupons = async (page = 1, kategori = '', search = '') => {
    try {
      setLoading(true);
      setError(null);
      
      const filters = {
        page,
        limit: 10,
      };
      
      if (kategori && kategori !== 'Semua') {
        filters.kategori = kategori;
      }
      
      if (search && search.trim()) {
        filters.search = search.trim();
      }
      
      const response = await getCoupons(filters);
      
      if (response.status === 'success') {
        const formattedCoupons = response.data.coupons.map((coupon) => {
          return {
            ...formatCouponForDisplay(coupon),
            original: coupon,
          };
        });
        
        if (page === 1) {
          setCoupons(formattedCoupons);
        } else {
          setCoupons(prev => [...prev, ...formattedCoupons]);
        }
        
        // Update pagination info
        const pagination = response.data.pagination;
        setCurrentPage(pagination.current_page);
        setTotalPages(pagination.total_pages);
        setHasMore(pagination.current_page < pagination.total_pages);
      }
    } catch (error) {
      console.error('Error loading coupons:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Load user's claimed coupons with pagination support
  const loadMyCoupons = async (page = 1) => {
    try {
      setLoadingMyCoupons(true);
      setError(null);
      
      const response = await getMyCoupons({ page, limit: 10 });
      
      if (response.status === 'success') {
        const formattedCoupons = response.data.coupons.map(formatClaimedCouponForDisplay);
        
        if (page === 1) {
          setMyCoupons(formattedCoupons);
        } else {
          setMyCoupons(prev => [...prev, ...formattedCoupons]);
        }
        
        // Update pagination info - only show Load More when total > 10 (total_pages > 1)
        const pagination = response.data.pagination || {};
        setMyCouponsCurrentPage(pagination.current_page ?? page);
        setMyCouponsTotalPages(pagination.total_pages ?? 1);
        setMyCouponsHasMore((pagination.current_page ?? page) < (pagination.total_pages ?? 1));
        
        console.log('My coupons loaded successfully:', formattedCoupons.length, 'items, page', page);
      }
    } catch (error) {
      console.error('Error loading my coupons:', error);
      setError(error.message);
    } finally {
      setLoadingMyCoupons(false);
    }
  };
  
  // Handle coupon claiming
  const handleClaimCoupon = async (couponId) => {
    try {
      
      setLoading(true);
      const response = await claimCoupon(couponId);
      
      if (response.status === 'success') {
        const claimedCoupon = buildClaimedCouponFromClaimResponse(response);
        const isPending = isPaygiftClaimPending(claimedCoupon);
        const modalUi = isPending ? getPaygiftClaimModalUi(claimedCoupon) : null;
        Alert.alert(
          isPending ? modalUi.title : 'Success!',
          isPending ? modalUi.headerMessage : (response.message || 'Coupon claimed successfully!'),
          [{ text: 'OK', onPress: () => {
            loadCoupons(1, selectedFilter, searchQuery);
            loadMyCoupons(1);
            navigateToClaimedCouponDetails(navigation, claimedCoupon);
          }}]
        );
      }
    } catch (error) {
      console.error('Error claiming coupon:', error);
      if (showPaygiftBlockedFromError(error)) {
        return;
      }
      Alert.alert('Error', error.message || 'Failed to claim coupon');
    } finally {
      setLoading(false);
    }
  };
  

  
  // Filter out claimed coupons from catalog by matching IDs with myCoupons
  const claimedCouponIds = myCoupons.map(coupon => coupon.id);
  const filteredCoupons = coupons.filter(coupon => !claimedCouponIds.includes(coupon.id) && !coupon.isEmptyCoupon);

  if (!fontsLoaded || !userData) {
    return (
      <View style={styles.loadingContainer}>
        <Text>Loading...</Text>
      </View>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      {/* Fixed Header Section */}
      <View style={styles.fixedHeader}>
        <SharedHeader 
          title="Coupon"
          onBackPress={() => navigation.goBack()}
          onNotificationPress={() => {/* Handle notification press */}}
        />
        
        {/* Tab Header */}
        <TabNavigation 
          tabs={tabs}
          activeTab={selectedTab}
          onTabPress={setSelectedTab}
        />

        {/* Spacer for where SearchFilter component was hidden */}
        <View style={styles.searchFilterSpacer} />

        {/* SearchFilter component hidden per user request
        <SearchFilter 
          searchQuery={searchQuery}
          onSearchChange={handleSearch}
          placeholder="Cari kupon disini"
          onFilterPress={() => {}}
        />
        */}
      </View>
      
      {/* Scrollable Content Section */}
      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        <View style={styles.contentSection}>
          
          {/* Tab Content */}
          {selectedTab === 'my-coupons' ? (
            <View style={styles.tabContent}>
              {loadingMyCoupons && myCoupons.length === 0 ? (
                <View style={styles.loadingContainer}>
                  <ActivityIndicator size="large" color="#3B82F6" />
                  <Text style={styles.loadingText}>Loading your coupons...</Text>
                </View>
              ) : myCoupons.length === 0 ? (
                <View>
                  <Text style={styles.emptyStateText}>Belum ada kupon yang dimiliki</Text>
                  <Text style={styles.emptyStateSubtext}>Tukarkan poin kamu dengan kupon menarik di katalog kupon</Text>
                </View>
              ) : (
                <View style={styles.couponsContainer}>
                  {myCoupons.map((coupon) => {
                    const couponThumb = getCouponImageSource(coupon);
                    return (
                    <View 
                      key={`my-${coupon.claimId}`} 
                      style={[
                        styles.couponCard,
                        coupon.status === 'expired' && styles.couponCardExpired
                      ]}
                    >
                      <View style={styles.couponImageContainer}>
                        {couponThumb ? (
                          <Image source={couponThumb} style={styles.couponImage} />
                        ) : (
                          <View style={styles.couponImagePlaceholder}>
                            <Ionicons name="receipt" size={32} color="#94A3B8" />
                          </View>
                        )}
                      </View>
                      <View style={styles.couponContent}>
                        <View style={styles.couponInfo}>
                          <View style={styles.couponBadges}>
                            {!isProductGiftCoupon(coupon) && !isDigitalRewardPending(coupon) && (coupon.couponCode || getDigitalRewardSn(coupon)) && (
                              <View style={styles.availabilityBadge}>
                                <Ionicons name="receipt" size={12} color="#3B82F6" />
                                <Text style={styles.availabilityBadgeText} numberOfLines={2}>
                                  {(() => {
                                    const sn = String(getDigitalRewardSn(coupon) || coupon.couponCode || '');
                                    const short = sn.length > 36 ? `${sn.slice(0, 36)}…` : sn;
                                    return `Code: ${short}`;
                                  })()}
                                </Text>
                              </View>
                            )}
                            <View style={[
                              styles.expiryBadge,
                              isProductGiftCoupon(coupon)
                                ? { backgroundColor: '#FEF3C7' }
                                : getDigitalRewardListStatusLabel(coupon)
                                ? { backgroundColor: '#FEF3C7' }
                                : getPaygiftListStatusLabel(coupon)
                                ? { backgroundColor: '#FEF3C7' }
                                : coupon.status === 'available' || coupon.status === 'claimed' ? styles.statusAvailable :
                                coupon.status === 'used' ? styles.statusUsed : styles.statusExpired
                            ]}>
                              <Text style={[
                                styles.expiryBadgeText,
                                isProductGiftCoupon(coupon)
                                  ? { color: '#92400E' }
                                  : getDigitalRewardListStatusLabel(coupon)
                                  ? { color: '#92400E' }
                                  : getPaygiftListStatusLabel(coupon)
                                  ? { color: '#92400E' }
                                  : coupon.status === 'available' || coupon.status === 'claimed' ? { color: '#065F46' } :
                                    coupon.status === 'used' ? { color: '#991B1B' } : { color: '#6B7280' }
                              ]}>
                                {isProductGiftCoupon(coupon)
                                  ? (getHadiahProdukValidationCode(coupon)
                                      ? `Kode: ${getHadiahProdukValidationCode(coupon)}`
                                      : 'Hadiah Produk')
                                  : getDigitalRewardListStatusLabel(coupon)
                                  || getPaygiftListStatusLabel(coupon)
                                    || (coupon.status === 'available' || coupon.status === 'claimed' ? 'Available' :
                                    coupon.status === 'used' ? 'Used' : 'Expired')}
                              </Text>
                            </View>
                            {coupon.endDate && (
                              <View style={styles.expiryDateBadge}>
                                <Ionicons name="calendar-outline" size={10} color="#6B7280" />
                                <Text style={styles.expiryDateText}>
                                  {new Date(coupon.endDate).toLocaleDateString('id-ID', { 
                                    day: 'numeric', 
                                    month: 'short', 
                                    year: 'numeric' 
                                  })}
                                </Text>
                              </View>
                            )}
                          </View>
                          <Text style={styles.couponTitle}>{coupon.title}</Text>
                          <Text style={styles.minOrderText}>{getDiscountText(coupon)}</Text>
                        </View>
                        
                        {/* Navigate to Details - Only show for non-expired coupons */}
                        {coupon.status !== 'expired' && (
                          <TouchableOpacity 
                            style={styles.viewDetailsButton}
                            onPress={() => {
                              // console.log('Coupon clicked:', coupon);
                              console.log('Coupon clicked');
                              // console.log('tipe_kupon:', coupon.tipe_kupon);
                              // console.log('type:', coupon.type);
                              // console.log('indobest:', coupon.indobest);
                              // console.log('isIndobest:', coupon.isIndobest);
                              
                              // Navigate to appropriate details screen based on coupon type
                              // Check all possible property names that could indicate an Indobest coupon
                              const isIndobestCoupon = 
                                coupon.tipe_kupon === 'indobest' || 
                                coupon.type === 'indobest' ||
                                coupon.indobest === true ||
                                coupon.isIndobest === true;
                              
                              // Check for Paygift coupons
                              const isPaygiftCoupon = 
                                coupon.tipe_kupon === 'paygift' ||
                                coupon.type === 'paygift' ||
                                coupon.paygift === true ||
                                coupon.isPaygift === true;
                              
                              const isHadiahKejutanCoupon = 
                                isProductGiftCoupon(coupon);
                              
                              if (isIndobestCoupon) {
                                console.log('Navigating to IndobestCouponDetails');
                                navigation.navigate('IndobestCouponDetails', { coupon });
                              } else if (isPaygiftCoupon) {
                                console.log('Navigating to PaygiftCouponDetails');
                                navigation.navigate('PaygiftCouponDetails', { coupon });
                              } else if (isHadiahKejutanCoupon) {
                                navigation.navigate('CouponDetails', { coupon });
                              } else {
                                console.log('Navigating to regular CouponDetails');
                                navigation.navigate('CouponDetails', { coupon });
                              }
                            }}
                          >
                            <Text style={styles.viewDetailsButtonText}>View Details</Text>
                          </TouchableOpacity>
                        )}
                      </View>
                    </View>
                  );})}
                  
                  {/* Load More - only show when total > 10 (total_pages > 1) */}
                  {myCouponsTotalPages > 1 && myCouponsHasMore && (
                    <TouchableOpacity 
                      style={styles.loadMoreButton}
                      onPress={() => loadMyCoupons(myCouponsCurrentPage + 1)}
                      disabled={loadingMyCoupons}
                    >
                      {loadingMyCoupons ? (
                        <ActivityIndicator size="small" color="#3B82F6" />
                      ) : (
                        <Text style={styles.loadMoreText}>Load More</Text>
                      )}
                    </TouchableOpacity>
                  )}
                </View>
              )}
            </View>
          ) : (
            <View style={styles.tabContent}>
              <Text style={styles.subtitle}>Pilihan kupon yang bisa kamu tukarkan</Text>
              
              {/* Spacer for where FilterBadges component was hidden */}
              <View style={styles.filterBadgesSpacer} />
              
              {/* Filter Badges - hidden per user request
              <FilterBadges 
                filters={filters}
                selectedFilter={selectedFilter}
                onFilterPress={handleFilterChange}
              />
              */}
              
              {/* Error State */}
              {error && (
                <View style={styles.errorContainer}>
                  <Text style={styles.errorText}>{error}</Text>
                  <TouchableOpacity 
                    style={styles.retryButton}
                    onPress={() => loadCoupons(1, selectedFilter, searchQuery)}
                  >
                    <Text style={styles.retryButtonText}>Retry</Text>
                  </TouchableOpacity>
                </View>
              )}
              
              {/* Loading State */}
              {loading && currentPage === 1 ? (
                <View style={styles.loadingContainer}>
                  <ActivityIndicator size="large" color="#3B82F6" />
                  <Text style={styles.loadingText}>Loading coupons...</Text>
                </View>
              ) : (
                <View style={styles.couponsContainer}>
                  {filteredCoupons.map((coupon) => {
                    const couponThumb = getCouponImageSource(coupon);
                    return (
                    <View 
                      key={`catalog-${coupon.id}`} 
                      style={styles.couponCard}
                    >
                      <TouchableOpacity onPress={() => {
                        if(coupon.externalURL !== null && coupon.externalURL != "")
                          {window.location.replace(coupon.externalURL)} 
                        else navigation.navigate('CouponDetails', { coupon: (coupon.original || coupon) })
                        }}>
                        <View style={styles.couponImageContainer}>
                          {couponThumb ? (
                            <Image source={couponThumb} style={styles.couponImage} />
                          ) : (
                            <View style={styles.couponImagePlaceholder}>
                              <Ionicons name="receipt" size={32} color="#94A3B8" />
                            </View>
                          )}
                        </View>
                        <View style={styles.couponContent}>
                          <View style={styles.couponInfo}>
                            <View style={styles.couponBadges}>
                              <View style={styles.availabilityBadge}>
                                <Ionicons name="receipt" size={12} color="#3B82F6" />
                                <Text style={styles.availabilityBadgeText}>
                                  {coupon.remainingQuota > 0 ? `Tersisa ${coupon.remainingQuota} voucher` : 'Kuota habis'}
                                </Text>
                              </View>
                              <View style={styles.expiryBadge}>
                                <Text style={styles.expiryBadgeText}>
                                  Berakhir {new Date(coupon.tanggal_berakhir || coupon.endDate).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })}
                                </Text>
                              </View>
                            </View>
                            <Text style={styles.couponTitle}>{coupon.title}</Text>
                            {coupon.minOrder > 0 && (
                              <Text style={styles.minOrderText}>
                                Min. transaksi {coupon.minOrder >= 1000000 ? `${coupon.minOrder / 1000000} jt` : `Rp ${coupon.minOrder.toLocaleString('id-ID')}`}
                              </Text>
                            )}
                          </View>
                        </View>
                      </TouchableOpacity>
                      
                  
                    </View>
                  );})}
                  
                  {/* Load More Button */}
                  {hasMore && (
                    <TouchableOpacity 
                      style={styles.loadMoreButton}
                      onPress={() => loadCoupons(currentPage + 1, selectedFilter, searchQuery)}
                      disabled={loading}
                    >
                      {loading ? (
                        <ActivityIndicator size="small" color="#3B82F6" />
                      ) : (
                        <Text style={styles.loadMoreText}>Load More</Text>
                      )}
                    </TouchableOpacity>
                  )}
                </View>
              )}
            </View>
          )}
        </View>
        
        <View style={styles.bottomSpacing} />
      </ScrollView>
      {paygiftBlockedModal}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8F9FA',
  },
  fixedHeader: {
    backgroundColor: '#F8F9FA',
    zIndex: 1000,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  content: {
    flex: 1,
  },
  profileSection: {
    position: 'relative',
    paddingHorizontal: 16,
    paddingTop: 44,
    paddingBottom: 16,
    height: 220,
    overflow: 'hidden',
  },

  avatar: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: 'rgba(248, 250, 252, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  profileTextContainer: {
    flex: 1,
  },
  profileGreeting: {
    fontSize: 16,
    fontWeight: '600',
    color: '#F8FAFC',
    marginBottom: 8,
    fontFamily: 'Poppins-SemiBold',
  },
  statsRow: {
    flexDirection: 'row',
    gap: 12,
  },
  statItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  statItemLabel: {
    fontSize: 12,
    color: '#94A3B8',
    fontFamily: 'Poppins',
  },
  statItemValue: {
    fontSize: 14,
    fontWeight: '700',
    color: '#22D3EE',
    fontFamily: 'Poppins-Bold',
  },

  contentSection: {
  },

  tabContent: {
    flex: 1,
    paddingHorizontal: 20,
    paddingBottom: 100
  },
  emptyStateText: {
    fontSize: 16,
    fontWeight: '500',
    color: '#1E293B',
    fontFamily: 'Poppins-Medium',
    textAlign: 'center',
    marginTop: 40,
    marginBottom: 8,
  },
  emptyStateSubtext: {
    fontSize: 14,
    color: '#64748B',
    fontFamily: 'Poppins',
    textAlign: 'center',
    lineHeight: 20,
    paddingHorizontal: 20,
  },
  subtitle: {
    fontSize: 12,
    fontWeight: '400',
    color: '#94A3B8',
    fontFamily: 'Poppins',
    lineHeight: 16,
    marginBottom: 16,
  },

  couponsContainer: {
    gap: 16,
  },
  couponCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    overflow: 'hidden',
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
    elevation: 3,
  },
  couponCardExpired: {
    backgroundColor: '#F9FAFB',
    opacity: 0.7,
  },
  couponImageContainer: {
    height: 160,
    backgroundColor: '#FFFFFF',
    padding: 2,
  },
  couponImagePlaceholder: {
    flex: 1,
    backgroundColor: '#E2E8F0',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 6,
  },
  couponImage: {
    width: '100%',
    height: '100%',
    resizeMode: 'contain',
  },
  couponContent: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 12,
    padding: 12,
  },
  couponInfo: {
    flex: 1,
    gap: 6,
  },
  couponBadges: {
    flexDirection: 'row',
    gap: 6,
    marginBottom: 6,
  },
  availabilityBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    backgroundColor: '#DBEAFE',
    borderRadius: 6,
    paddingHorizontal: 4,
    paddingVertical: 2,
  },
  availabilityBadgeText: {
    fontSize: 10,
    fontWeight: '500',
    color: '#3B82F6',
    fontFamily: 'Poppins-Medium',
    lineHeight: 16,
  },
  expiryBadge: {
    backgroundColor: '#F1F5F9',
    borderRadius: 6,
    paddingHorizontal: 4,
    paddingVertical: 2,
  },
  expiryBadgeText: {
    fontSize: 10,
    fontWeight: '500',
    color: '#64748B',
    fontFamily: 'Poppins-Medium',
    lineHeight: 16,
  },
  expiryDateBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: '#F8FAFC',
    borderRadius: 6,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  expiryDateText: {
    fontSize: 10,
    fontWeight: '400',
    color: '#64748B',
    fontFamily: 'Poppins',
    lineHeight: 16,
  },
  couponTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#000000',
    fontFamily: 'Poppins-SemiBold',
    lineHeight: 24,
  },
  couponCode: {
    fontSize: 12,
    fontWeight: '400',
    color: '#6B7280',
    fontFamily: 'Poppins',
    marginBottom: 4,
  },
  couponDescription: {
    fontSize: 12,
    fontWeight: '400',
    color: '#374151',
    fontFamily: 'Poppins',
    lineHeight: 16,
    marginBottom: 8,
  },
  couponDetails: {
    marginBottom: 8,
  },
  availabilityText: {
    fontSize: 11,
    fontWeight: '500',
    color: '#059669',
    fontFamily: 'Poppins-Medium',
    marginBottom: 2,
  },
  minOrderText: {
    fontSize: 14,
    fontWeight: '400',
    color: '#94A3B8',
    fontFamily: 'Poppins',
    lineHeight: 20,
  },
  couponStatus: {
    alignSelf: 'flex-start',
  },
  statusText: {
    fontSize: 10,
    fontWeight: '600',
    fontFamily: 'Poppins-SemiBold',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    overflow: 'hidden',
  },
  statusAvailable: {
    backgroundColor: '#D1FAE5',
    color: '#065F46',
  },
  statusUsed: {
    backgroundColor: '#FEE2E2',
    color: '#991B1B',
  },
  statusExpired: {
    backgroundColor: '#F3F4F6',
    color: '#6B7280',
  },
  pointsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  coinContainer: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: '#FEF3C7',
    borderWidth: 1,
    borderColor: '#FBBF24',
    justifyContent: 'center',
    alignItems: 'center',
  },
  coin: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  pointsText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#F59E0B',
    fontFamily: 'Poppins-SemiBold',
  },
  redeemButton: {
    backgroundColor: '#3B82F6',
    borderRadius: 6,
    paddingVertical: 8,
    paddingHorizontal: 16,
    alignSelf: 'flex-start',
  },
  redeemButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#FFFFFF',
    fontFamily: 'Poppins-Medium',
  },
  redeemButtonDisabled: {
    backgroundColor: '#9CA3AF',
  },
  redeemButtonTextDisabled: {
    color: '#F3F4F6',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 40,
  },
  loadingText: {
    fontSize: 14,
    fontWeight: '400',
    color: '#6B7280',
    fontFamily: 'Poppins',
    marginTop: 12,
  },
  errorContainer: {
    backgroundColor: '#FEE2E2',
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
    alignItems: 'center',
  },
  errorText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#DC2626',
    fontFamily: 'Poppins-Medium',
    textAlign: 'center',
    marginBottom: 12,
  },
  retryButton: {
    backgroundColor: '#DC2626',
    borderRadius: 6,
    paddingVertical: 8,
    paddingHorizontal: 16,
  },
  retryButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#FFFFFF',
    fontFamily: 'Poppins-Medium',
  },

  loadMoreButton: {
    backgroundColor: '#F1F5F9',
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 24,
    alignItems: 'center',
    marginTop: 16,
  },
  loadMoreText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#3B82F6',
    fontFamily: 'Poppins-Medium',
  },
  bottomSpacing: {
    height: 20,
  },
  viewDetailsButton: {
    backgroundColor: '#059669',
    borderRadius: 6,
    paddingVertical: 8,
    paddingHorizontal: 16,
    alignItems: 'center',
    marginTop: 8,
  },
  viewDetailsButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
    fontFamily: 'Poppins-SemiBold',
  },
  searchFilterSpacer: {
    height: 16,
    marginBottom: 8,
  },
  filterBadgesSpacer: {
    height: 12,
    marginBottom: 16,
  },
});